import { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { F0Box, F0Button, F0TagRaw } from '@factorialco/f0-react'
import { ResourceHeader } from '@factorialco/f0-react/dist/experimental'
import { Delete, MoveDown, Organization, Pencil } from '@factorialco/f0-react/icons/app'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import DeleteJobCatalogTreeNodeModal from 'modules/job_catalog/components/DeleteJobCatalogTreeNodeModal'
import MoveJobCatalogTreeNodeModal from 'modules/job_catalog/components/MoveJobCatalogTreeNodeModal'
import NodeModal from 'modules/job_catalog/components/NodeModal'
import { useNodeModalContent } from 'modules/job_catalog/hooks/useModalContent'
import { useTreeNode } from 'modules/job_catalog/hooks/useTreeNode'
import useUpdateJobCatalogTreeNode from 'modules/job_catalog/hooks/useUpdateJobCatalogTreeNode'
import {
  NODE_TYPES,
  NODE_UUID_TYPES,
  NodeFormValues,
  TreeNode,
  countNodeTypes,
  findNodeByUuid,
  formatUuidToNodeType,
  getNavigationParentRoute,
  getParentChain,
  getTextFromNodeType,
  getTextFromNodeTypeSummary,
  isNodeType,
  maximizeClickPath,
  nodeTypeIcon,
  normalizeNodeType,
} from 'modules/job_catalog/lib/nodesUtils'
import { Salary } from 'modules/job_catalog/lib/salary'
import {
  formatSalaryFromCentsObject,
  getPreferredSalary,
  getSummarizedSalariesByCurrency,
} from 'modules/job_catalog/lib/salaryUtils'

type Props = {
  name: string
  description: string
  type: string
  canEditRole: boolean
  canManageTree?: boolean
  childrens?: TreeNode[]
  salaries?: Salary[]
  currentNode?: TreeNode
  isMaximized?: boolean
}

export const NodeHeader = ({
  name,
  description,
  type,
  childrens = [],
  salaries = [],
  canEditRole,
  canManageTree = false,
  currentNode,
  isMaximized = false,
}: Props) => {
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const { company } = useCurrentSession()
  const navigationParams = useParams()
  const { rootId, familyId, functionId, roleId, levelId, id } = navigationParams
  const nodeId = levelId || roleId || functionId || familyId || rootId || id || ''
  const normalizedType = normalizeNodeType(type)
  const [showEditNodeModal, setShowEditNodeModal] = useState(false)
  const [showDeleteNodeModal, setDeleteNodeModal] = useState(false)
  const [showMoveNodeModal, setShowMoveNodeModal] = useState(false)
  const modalContent = useNodeModalContent('edit', name, normalizedType)
  const typeLabel = isNodeType(type) ? getTextFromNodeType(normalizedType) : 'Unknown'
  const childrenTypeCounts = countNodeTypes(childrens)
  const { updateNode } = useUpdateJobCatalogTreeNode()
  const summarizedSalariesByCurrency = useMemo(
    () => getSummarizedSalariesByCurrency(salaries),
    [salaries]
  )
  const preferredSalary = useMemo(
    () => getPreferredSalary(summarizedSalariesByCurrency),
    [summarizedSalariesByCurrency]
  )
  const otherCurrencySalaries = useMemo(
    () => summarizedSalariesByCurrency.filter((salary) => salary.id !== preferredSalary?.id),
    [summarizedSalariesByCurrency, preferredSalary]
  )
  const formattedSalary = useMemo(() => {
    if (!preferredSalary) return i18n.t('job_catalog.nodes.no_salary')

    return formatSalaryFromCentsObject(preferredSalary)
  }, [preferredSalary])
  const nodeToUse = useMemo(() => {
    if (currentNode) return currentNode
    if (!nodeId || !childrens.length) return undefined
    return findNodeByUuid(childrens, nodeId)
  }, [currentNode, childrens, nodeId])

  const canDeleteNode = useMemo(
    () => nodeToUse?.type !== NODE_UUID_TYPES.ROOT && !isMaximized,
    [nodeToUse, isMaximized]
  )

  const { nodeType } = useTreeNode(nodeId)

  const canMoveNode = useMemo(
    () =>
      (nodeToUse?.type === NODE_UUID_TYPES.FUNCTION || nodeToUse?.type === NODE_UUID_TYPES.ROLE) &&
      !isMaximized,
    [nodeToUse, isMaximized]
  )

  const canEditNode = canManageTree && canEditRole
  const canViewGraph = canEditRole
  const canMoveTreeNode = canManageTree && canMoveNode
  const canDeleteTreeNode = canManageTree && canDeleteNode

  const metadata = [
    {
      label: typeLabel,
      hideLabel: true,
      value: {
        type: 'text',
        content: (
          <F0TagRaw
            text={typeLabel}
            icon={nodeTypeIcon(isNodeType(type) ? type : NODE_TYPES.ROOT)}
          />
        ),
      },
    },
    ...Object.entries(childrenTypeCounts)
      .filter(
        ([childType]) =>
          normalizeNodeType(childType) !== NODE_TYPES.LEVEL ||
          normalizeNodeType(nodeType) === NODE_TYPES.ROLE
      )
      .map(([childType, count]) => ({
        label: isNodeType(childType) ? getTextFromNodeTypeSummary(childType, count) : 'Unknown',
        value: {
          type: 'text' as const,
          content: (
            <F0Box display="flex" flexDirection="row" justifyContent="center" gap="xs">
              {count}
            </F0Box>
          ),
        },
      })),
    {
      label: i18n.t('job_catalog.nodes.salary'),
      value: {
        type: 'data-list',
        data: [
          formattedSalary,
          ...otherCurrencySalaries.map((salary) => formatSalaryFromCentsObject(salary)),
        ],
      },
    },
  ].filter(Boolean)

  const handleMaximizeClick = () => {
    if (!nodeToUse || !nodeId) return

    navigate(
      maximizeClickPath(
        getParentChain(nodeToUse),
        normalizeNodeType(nodeToUse.type),
        nodeId,
        navigation,
        nodeToUse.type
      )
    )
  }

  const handleMinimizeClick = () => {
    const treeNodeId = nodeToUse?.node_uuid || ''
    navigate(
      navigation.employees.jobCatalog.overview.node.fullPath({
        id: treeNodeId,
      })
    )
  }

  const handleViewClick = () => {
    track(trackingEvents.jobCatalog.goToNodeDetailsSubmit, {
      event_category: 'Organizations',
      node_type: normalizedType,
      node_id: nodeId,
      node_name: name,
    })
    if (isMaximized) {
      handleMaximizeClick()
    } else {
      handleMinimizeClick()
    }
  }

  const handleEditNode = async ({
    name: editedName,
    description: editedDescription,
  }: NodeFormValues) => {
    const result = await updateNode(
      company.id,
      editedDescription,
      editedName,
      nodeToUse?.id ?? '0',
      formatUuidToNodeType(type),
      nodeToUse?.ancestor_id ?? '0',
      nodeToUse?.node_uuid || ''
    )
    const handled = handleResult(result, (errors) => {
      app.setFlash({
        type: 'wrong',
        message: errors.errors || i18n.t('errors.generic_error'),
      })
    })
    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('job_catalog.nodes.modal.edit_success', {
          type: getTextFromNodeType(type),
        }),
        time: 5000,
      })
    }
    setShowEditNodeModal(false)
  }
  const handleDeleteNode = (success: boolean) => {
    setDeleteNodeModal(false)
    if (success) {
      if (!nodeToUse?.type) {
        navigate(navigation.employees.jobCatalog.overview.fullPath())
      } else {
        navigate(getNavigationParentRoute(nodeId, nodeToUse?.type, navigation, navigationParams))
      }
    }
  }

  const handleMoveNode = (success: boolean) => {
    setShowMoveNodeModal(false)
    if (success) {
      navigate(
        getNavigationParentRoute(
          nodeId,
          nodeToUse?.type || NODE_UUID_TYPES.ROOT,
          navigation,
          navigationParams
        )
      )
    }
  }
  const initialValues: NodeFormValues = {
    name: nodeToUse?.name || '',
    description: nodeToUse?.description || '',
    children: [],
  }

  return (
    <F0Box display="flex" flexDirection="row" justifyContent="between">
      <F0Box grow>
        <ResourceHeader
          title={name}
          description={description}
          // @ts-expect-error Element vs string type mismatch in metadata
          metadata={metadata}
          secondaryActions={[
            {
              label: i18n.t('job_catalog.nodes.actions.view_graph'),
              icon: Organization,
              isVisible: canViewGraph,
              onClick: handleViewClick,
            },
            {
              label: i18n.t('common.edit'),
              icon: Pencil,
              isVisible: canEditNode,
              onClick: () => setShowEditNodeModal(true),
            },
          ]}
          otherActions={[
            {
              label: i18n.t('job_catalog.nodes.move.button', { nodeType: normalizedType }),
              icon: MoveDown,
              onClick: () => setShowMoveNodeModal(true),
              isVisible: canMoveTreeNode,
            },
            {
              type: 'separator',
              isVisible: canMoveTreeNode && canDeleteTreeNode,
            },
            {
              critical: true,
              icon: Delete,
              label: i18n.t('common.delete_type', { type: typeLabel.toLowerCase() }),
              onClick: () => {
                setDeleteNodeModal(true)
              },
              isVisible: canDeleteTreeNode,
            },
          ]}
        />
      </F0Box>
      {isMaximized && (
        <F0Box paddingRight="lg" paddingTop="sm">
          <F0Button
            label={i18n.t('job_catalog.nodes.actions.view_details')}
            onClick={handleViewClick}
            round
            size="md"
            variant="outline"
          />
        </F0Box>
      )}
      {showEditNodeModal && (
        <NodeModal
          onClose={() => setShowEditNodeModal(false)}
          title={modalContent.title}
          description={modalContent.description}
          onSubmit={handleEditNode}
          icon={modalContent.icon || 'pencil'}
          nodeType={normalizedType}
          initialValues={initialValues}
          actionType="edit"
        />
      )}
      {nodeToUse && showDeleteNodeModal && (
        <DeleteJobCatalogTreeNodeModal
          nodeId={nodeId}
          nodeName={nodeToUse.name}
          nodeType={normalizedType}
          childrenNodeType={nodeToUse.children[0]?.type || ''}
          onSubmit={handleDeleteNode}
          onClose={() => setDeleteNodeModal(false)}
          // TODO: this can be set to something like nodeToUse.people.length === 0 when we can have the people count precalculated
          //canBeDeleted={nodeToUse.people_count === 0}
          canBeDeleted
        />
      )}

      {nodeToUse && showMoveNodeModal && (
        <MoveJobCatalogTreeNodeModal
          nodeName={nodeToUse.name}
          onSubmit={handleMoveNode}
          onClose={() => setShowMoveNodeModal(false)}
        />
      )}
    </F0Box>
  )
}
